import { httpClient } from "./httpClient";
import _, { sumBy, uniq } from "lodash-es";
import parseISO from "date-fns/parseISO";
import addDays from "date-fns/addDays";
import { dlrTsgErrorMap, mmsErrorCodeMap } from "~/utils/dlrErrorCodes";

type useDeliverabilityArgs = {
  start_date: string;
  end_date: string;
};

type DeliverabilityRecord = {
  date: string;
  error_code: string;
  count: number;
};

type AccumulatedChartData = Record<string, Record<string, number>>;

type DeliverabilityConfig = {
  endpoint: string;
  responseKey: string;
  deliveredCodes: string[];
  labelFn: (code: string) => string;
};

const smsConfig: DeliverabilityConfig = {
  endpoint: "/dlr-statistics",
  responseKey: "dlr_statistics",
  deliveredCodes: ["000", "3ec"],
  labelFn: (errorCode: string) =>
    dlrTsgErrorMap[errorCode]
      ? `${errorCode} - ${dlrTsgErrorMap[errorCode]}`
      : errorCode,
};

// MMS Error codes: "expired", "retrieved", "rejected", "deferred",
// "indeterminate", "forwarded", "unrecognised"
// "retrieved" and "forwarded" are considered delivered statuses
const mmsConfig: DeliverabilityConfig = {
  endpoint: "/mms-dlr-statistics",
  responseKey: "mms_dlr_statistics",
  deliveredCodes: ["retrieved", "forwarded"],
  labelFn: (code: string) => mmsErrorCodeMap[code] || code,
};

export async function getDeliverabilityRecords({
  start_date,
  end_date,
}: useDeliverabilityArgs): Promise<any> {
  return getDeliverabilityRecordsWithConfig(
    { start_date, end_date },
    smsConfig
  );
}

export async function getMmsDeliverabilityRecords({
  start_date,
  end_date,
}: useDeliverabilityArgs): Promise<any> {
  return getDeliverabilityRecordsWithConfig(
    { start_date, end_date },
    mmsConfig
  );
}

async function getDeliverabilityRecordsWithConfig(
  { start_date, end_date }: useDeliverabilityArgs,
  config: DeliverabilityConfig
): Promise<any> {
  const response = await httpClient(config.endpoint, {
    method: "GET",
    parameters: {
      start_date,
      end_date,
    },
  });

  const dlrStatistics: DeliverabilityRecord[] =
    response.body[config.responseKey];

  const isDeliveredCode = (code: string) =>
    config.deliveredCodes.includes(code.toLowerCase());

  // gather all the available error codes in the dataset
  const errorCodes = uniq(dlrStatistics.map((record) => record.error_code)).map(
    (errCode) => ({
      label: config.labelFn(errCode),
      value: errCode,
    })
  );

  // calculate the error and non error records so we can display percentages
  const totalErrorRecords = dlrStatistics.reduce((acc, i) => {
    if (isDeliveredCode(i.error_code)) return acc;
    return acc + i.count;
  }, 0);

  const totalRecords = dlrStatistics.reduce((acc, i) => acc + i.count, 0);

  const totalNonErrorRecords = totalRecords - totalErrorRecords;
  const deliverabilityPercentage = totalRecords
    ? (totalNonErrorRecords / totalRecords) * 100
    : undefined;

  // transform the data so we can display it in charts
  // we'll first initialize the accumulator so we don't have any gaps in the dates/errors
  const dateRange = generateDateRange(start_date, end_date);
  const initialAccumulatorMap: AccumulatedChartData = {};
  for (const date of dateRange) {
    initialAccumulatorMap[date] = {};
    for (const error of errorCodes) {
      initialAccumulatorMap[date][error.value] = 0;
    }
  }

  // Then we'll generate the deliverability map so the chart can calculate
  // and display the data more easily.
  const deliverabilityRecords = dlrStatistics.reduce(
    (acc, curr: DeliverabilityRecord) => {
      acc[curr.date][curr.error_code] += curr.count;
      return acc;
    },
    initialAccumulatorMap
  );

  // Here we'll take the counts that we previously calculated from deliverabilityRecords
  // and convert them to ratios (so we can display them as percentages).
  const chartData = Object.keys(
    deliverabilityRecords
  ).reduce<AccumulatedChartData>((acc, date) => {
    const nonDeliveredCodes = Object.keys(deliverabilityRecords[date]).filter(
      (errorCode) => !isDeliveredCode(errorCode)
    );
    const totalMessagesForDate = Object.values(
      deliverabilityRecords[date]
    ).reduce((acc, c) => acc + c, 0);

    acc[date] = nonDeliveredCodes.reduce<Record<string, number>>(
      (innerAcc, errorCode) => {
        // zero checks ensure we don't end up with NaN values
        innerAcc[errorCode] =
          totalMessagesForDate !== 0
            ? deliverabilityRecords[date][errorCode] / totalMessagesForDate
            : 0;
        return innerAcc;
      },
      {}
    );

    const totalDeliveredMessagesForDate = config.deliveredCodes.reduce(
      (sum, code) => sum + (deliverabilityRecords[date][code] ?? 0),
      0
    );
    // zero checks ensure we don't end up with NaN values
    acc[date]["Delivered"] =
      totalMessagesForDate !== 0
        ? totalDeliveredMessagesForDate / totalMessagesForDate
        : 0;

    return acc;
  }, {});

  const errorCodeCountMap = _.chain(dlrStatistics)
    .groupBy("error_code")
    .reduce<{ name: string; count: number; percentage: number }[]>(
      (acc, records) => {
        const errorCode = records[0].error_code;
        const count = sumBy(records, (r) => r.count);
        const percentage = totalRecords !== 0 ? count / totalRecords : 0;

        // --- Delivered codes (e.g. "000", "3ec") roll up into a single "Delivered" row ---
        if (isDeliveredCode(errorCode)) {
          const idx = acc.findIndex((el) => el.name === "Delivered");
          if (idx !== -1) {
            acc[idx].count += count;
            acc[idx].percentage += percentage;
          } else {
            acc.push({ name: "Delivered", count, percentage });
          }
          return acc;
        }

        // --- Error codes under 1% roll up into a single "Other" row ---
        if (percentage * 100 < 1) {
          const idx = acc.findIndex((el) => el.name === "Other");
          if (idx !== -1) {
            acc[idx].count += count;
            acc[idx].percentage += percentage;
          } else {
            acc.push({ name: "Other", count, percentage });
          }
          return acc;
        }

        // --- All other error codes get their own row ---
        acc.push({
          name: config.labelFn(errorCode),
          count,
          percentage,
        });
        return acc;
      },
      []
    )
    .value()
    .sort((a, b) => b.count - a.count);

  const filteredErrorCodes = [{ label: "Delivered", value: "Delivered" }]
    .concat(errorCodes)
    .filter((code) => !isDeliveredCode(code.value));

  return {
    errorCodeCountMap,
    deliverabilityPercentage,
    deliverabilityRecords: chartData,
    errorCodes: filteredErrorCodes,
  };
}

/**
 * Takes 2 dates in "YYYY-MM-DD" format and returns the list of all the dates in between
 * including the start and end date, the list is in the "YYYY-MM-DD" format.
 */
function generateDateRange(startDate: string, endDate: string): string[] {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  const dateArray = [];

  let currentDate = start;
  while (currentDate <= end) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1; // getMonth returns a 0-based value
    const day = currentDate.getDate();
    const formattedDate = `${year}-${month.toString().padStart(2, "0")}-${day
      .toString()
      .padStart(2, "0")}`;
    dateArray.push(formattedDate);
    currentDate = addDays(currentDate, 1);
  }

  return dateArray;
}
